import { isNil, sumBy } from 'lodash';
import last from 'lodash/last';

import {
  getFixedChargeLvosForUnit,
  getProvisionedChargeLvosForUnit,
  hasFixedChargeLvoForUnit,
} from './accounting/chargesUtils';
import { findCustomValue } from './customValuesUtils';
import {
  toCalendarDate,
  calendarDateFromTimestamp,
  today,
  isAfter,
  isBefore,
  CalendarDate,
  isAfterOrEqual,
  formatCalendarDate,
  formatCalendarMonth,
  formatCalendarMonthAndYear,
  setCalendarDate,
  addCalendarDate,
  subtractCalendarDate,
  startOfCalendarMonth,
} from './dateutils';
import { EnumMessagesEN } from './i18n/enumMessages';
import { type CustomIndexationParams, getNewIndexMonth, isViableCustomIndexation } from './indexationIndexMonthUtils';
import {
  ChargeIndexationBase,
  getChargeIndexationBase,
  sumChargeIndexationBaseAmountsFromDetail,
  isChargeLineIndexed,
} from './leasePriceHistoryUtils';
import { getLeaseMonthDueDate, LeaseExtended } from './leaseutils';
import {
  formatNumber,
  formatNumberToEuroCurrency,
  formatNumberToPercent,
  roundAtSecondDecimal,
  roundToDecimalPlace,
} from './numbers';
import { isNilOrEmpty } from './objectUtils';
import { TechnicCustomValue } from './technicsUtils';
import {
  LeaseAmountUpdateStatus,
  LeaseDTO,
  LeasePaymentInvoicePeriod,
  LeasePriceHistoryStatus,
  LeasePriceHistoryType,
  LeaseVariousOperationDTO,
  UnitLeaseDTO,
  UnitType,
} from './types/dto-app';
import {
  BaseIndexType,
  Building,
  IndexType,
  Language,
  Lease,
  LeasePriceHistory,
  LeasePriceHistoryDetail,
  LeaseVariousOperation,
  Technic,
  UnitLease,
} from './types/model-app';

export enum BaseYear {
  YEAR_1914 = 1914,
  YEAR_1936 = 1936,
  YEAR_1953 = 1953,
  YEAR_1966 = 1966,
  YEAR_1971 = 1971,
  YEAR_1974 = 1974,
  YEAR_1981 = 1981,
  YEAR_1988 = 1988, // START HEALTH
  YEAR_1996 = 1996,
  YEAR_2004 = 2004, // START WITHOUT ENERGY
  YEAR_2013 = 2013,
  YEAR_2025 = 2025,
}

// Per Statbel, a base-year series only becomes legally applicable to leases signed from January of
// the year after its label (confirmed for the 2013 → 2025 transition). Applied consistently to the
// older transitions below; revisit 1988/1996/2004 exact cutover months if a dispute surfaces for those eras.
let baseYearEffectiveFromCache: Record<BaseYear, CalendarDate> | undefined;
export const getBaseYearEffectiveFrom = (): Record<BaseYear, CalendarDate> =>
  (baseYearEffectiveFromCache ??= {
    [BaseYear.YEAR_1914]: toCalendarDate('1914-01-01'),
    [BaseYear.YEAR_1936]: toCalendarDate('1936-01-01'),
    [BaseYear.YEAR_1953]: toCalendarDate('1953-01-01'),
    [BaseYear.YEAR_1966]: toCalendarDate('1966-01-01'),
    [BaseYear.YEAR_1971]: toCalendarDate('1971-01-01'),
    [BaseYear.YEAR_1974]: toCalendarDate('1974-01-01'),
    [BaseYear.YEAR_1981]: toCalendarDate('1981-01-01'),
    [BaseYear.YEAR_1988]: toCalendarDate('1988-01-01'),
    [BaseYear.YEAR_1996]: toCalendarDate('1997-01-01'),
    [BaseYear.YEAR_2004]: toCalendarDate('2005-01-01'),
    [BaseYear.YEAR_2013]: toCalendarDate('2014-01-01'),
    [BaseYear.YEAR_2025]: toCalendarDate('2026-01-01'),
  });

export enum EnergeticPerformance {
  A_PLUS_PLUS = 'A++',
  A_PLUS = 'A+',
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
  F = 'F',
  G = 'G',
}

/** Weights of the EPB correction factor formulas on the two health indexes, as the indexation engine applies them. */
export const EPB_CORRECTION_FIRST_DEGREE_WEIGHT = 0.5;
export const EPB_CORRECTION_WALLONIA_OLDEST_INDEX_WEIGHT = 0.25;
export const EPB_CORRECTION_WALLONIA_NEWEST_INDEX_WEIGHT = 0.75;

export const DECLINED_PRICE_CHANGE_STATUSES = [
  LeaseAmountUpdateStatus.TO_PROCESS_CLIENT_REFUSED,
  LeaseAmountUpdateStatus.REFUSED,
];

export const EPB_GRADES = [
  EnergeticPerformance.A_PLUS_PLUS,
  EnergeticPerformance.A_PLUS,
  EnergeticPerformance.A,
  EnergeticPerformance.B,
  EnergeticPerformance.C,
  EnergeticPerformance.D,
  EnergeticPerformance.E,
  EnergeticPerformance.F,
  EnergeticPerformance.G,
] as const;

export type EpbGrade = (typeof EPB_GRADES)[number];

export const generateDateTimeValues = (lease: Lease, language?: Language | null) => {
  const leaseStartDate = toCalendarDate(lease.startDate);
  const currentYear = today().year;

  const leaseStartDateThisYear = setCalendarDate(leaseStartDate, currentYear, 'year');
  const startOfLeaseMonthThisYear = startOfCalendarMonth(leaseStartDateThisYear);

  const startOfNextLeaseMonth = addCalendarDate(startOfLeaseMonthThisYear, 1, 'months');
  const endOfLeaseMonthThisYear = subtractCalendarDate(startOfNextLeaseMonth, 1, 'days');

  const formatLanguage = language ?? Language.EN;

  return {
    month: formatCalendarMonth(leaseStartDateThisYear, formatLanguage),
    startOfMonthDate: formatCalendarDate(startOfLeaseMonthThisYear, formatLanguage),
    currentDate: formatCalendarDate(leaseStartDateThisYear, formatLanguage),
    endOfMonthDate: formatCalendarDate(endOfLeaseMonthThisYear, formatLanguage),
    thisMonthDate: formatCalendarMonthAndYear(startOfLeaseMonthThisYear, formatLanguage),
    thisMonthNextYearDate: formatCalendarMonthAndYear(
      addCalendarDate(startOfLeaseMonthThisYear, 1, 'years'),
      formatLanguage
    ),
  };
};

/**
 * This function will apply the indexation formula with given params.
 * @param baseIndex
 * @param newIndex
 * @param initialPrice
 * @returns
 */
export const getIndexedPrice = (baseIndex: number, newIndex: number, initialPrice: number) => {
  const indexedPrice = (initialPrice * newIndex) / baseIndex;
  return roundAtSecondDecimal(indexedPrice);
};

/**
 * Based on the indexation formula, give the new indexed price for rent
 * @param lease
 * @param leasePriceHistory
 * @returns
 */
export const computeRentalIndexedPrice = (lease: LeaseDTO, leasePriceHistory: LeasePriceHistory) => {
  if (isNilOrEmpty(leasePriceHistory.amountDetails) || isNil(lease.units) || isNilOrEmpty(lease.units.items)) {
    return 0;
  }
  const mainUnitLease = lease.units.items.find((unitLease) => unitLease && unitLease.mainUnit);
  if (!mainUnitLease) {
    return 0;
  }

  const amountForMainUnit = leasePriceHistory.amountDetails.find(
    (amountDetail) => amountDetail.unitId === mainUnitLease.unitId
  );
  if (!amountForMainUnit) {
    return 0;
  }

  return getIndexedPrice(
    amountForMainUnit.indexationBaseIndex ?? 100,
    amountForMainUnit.indexationNewIndex ?? 100,
    mainUnitLease.initialRentalPrice!
  );
};

/**
 * Each charge has its own base since REN-9014: the unit total sums the indexed charges, not one summed ratio. A record
 * written before carries no per-line base and keeps the one ratio it was computed with, down to the cent.
 */
export const sumIndexedFixedChargesOfDetail = (
  amountDetail: LeasePriceHistoryDetail,
  chargeIndexationBaseAmounts: Map<string, number>
): number => {
  const newIndex = amountDetail.indexationNewIndex ?? 100;
  const isWrittenBeforePerLineBase = (amountDetail.chargesAmountsDetails ?? []).every(
    (chargeAmountsDetail) => isNil(chargeAmountsDetail.indexed) && isNil(chargeAmountsDetail.indexationBaseIndex)
  );
  if (isWrittenBeforePerLineBase) {
    return getIndexedPrice(
      amountDetail.indexationBaseIndex ?? 100,
      newIndex,
      sumChargeIndexationBaseAmountsFromDetail(amountDetail, chargeIndexationBaseAmounts)
    );
  }
  return roundAtSecondDecimal(
    sumBy(amountDetail.chargesAmountsDetails ?? [], (chargeAmountsDetail) => {
      if (!isChargeLineIndexed(chargeAmountsDetail)) {
        return chargeAmountsDetail.previousMonthlyAmount;
      }
      const { baseIndex, baseAmount } = getChargeIndexationBase(
        amountDetail,
        chargeAmountsDetail,
        chargeIndexationBaseAmounts
      );
      return getIndexedPrice(baseIndex ?? 100, newIndex, baseAmount);
    })
  );
};

/**
 * The stored EPB total covers the indexed lines only: a line the run did not index keeps its previous amount on top.
 */
export const getEpbReducedChargesTotalOfDetail = (
  amountDetail: Pick<LeasePriceHistoryDetail, 'chargesIndexedPriceAfterNewEpbReduction' | 'chargesAmountsDetails'>
): number | null | undefined => {
  const storedTotal = amountDetail.chargesIndexedPriceAfterNewEpbReduction;
  if (isNil(storedTotal)) {
    return storedTotal;
  }
  const notIndexedAmount = sumBy(
    (amountDetail.chargesAmountsDetails ?? []).filter(
      (chargeAmountsDetail) => !isChargeLineIndexed(chargeAmountsDetail)
    ),
    (chargeAmountsDetail) => chargeAmountsDetail.previousMonthlyAmount
  );
  return roundAtSecondDecimal(storedTotal + notIndexedAmount);
};

/**
 * Based on the indexation formula, give the new indexed price for fixed charges
 * @param lease
 * @param leasePriceHistory
 * @returns
 */
export const computeFixedChargeIndexedPrice = (
  lease: LeaseDTO,
  leasePriceHistory: LeasePriceHistory,
  chargeIndexationBaseAmounts: Map<string, number>
) => {
  if (isNilOrEmpty(leasePriceHistory.amountDetails) || isNil(lease.units) || isNilOrEmpty(lease.units.items)) {
    return 0;
  }
  const mainUnitLease = lease.units.items.find((unitLease) => unitLease && unitLease.mainUnit);
  if (!mainUnitLease) {
    return 0;
  }
  const hasFixedCharges = hasFixedChargeLvoForUnit(
    (lease.variousOperations?.items ?? []).filter((lvo) => !isNil(lvo)) as LeaseVariousOperation[],
    mainUnitLease.unitId
  );
  if (!hasFixedCharges) {
    return 0;
  }

  const amountForMainUnit = leasePriceHistory.amountDetails.find(
    (amountDetail) => amountDetail.unitId === mainUnitLease.unitId
  );
  if (!amountForMainUnit) {
    return 0;
  }

  return sumIndexedFixedChargesOfDetail(amountForMainUnit, chargeIndexationBaseAmounts);
};

export const getIndexationMixedPrice = (
  startDate: CalendarDate,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod,
  paymentInvoicePeriodCustomDay: number | null | undefined,
  indexedPrice: number,
  currentPrice: number
): number => {
  const todayDate = today();
  const startOfTodaysMonth = startOfCalendarMonth(todayDate);

  const leaseDueDate = getLeaseMonthDueDate(
    startDate,
    paymentInvoicePeriod,
    paymentInvoicePeriodCustomDay,
    startOfTodaysMonth
  );

  const startOfNextMonth = addCalendarDate(startOfTodaysMonth, 1, 'months');

  const numberOfDaysForOldPrice = startOfTodaysMonth.until(leaseDueDate).days;
  const numberOfDaysForNewPrice = leaseDueDate.until(startOfNextMonth).days;

  const numberOfDaysInMonth = todayDate.daysInMonth;

  const currentPricePerDay = currentPrice / numberOfDaysInMonth;
  const indexedPricePerDay = indexedPrice / numberOfDaysInMonth;

  return roundAtSecondDecimal(
    currentPricePerDay * numberOfDaysForOldPrice + indexedPricePerDay * numberOfDaysForNewPrice
  );
};

export enum IndexationRowStatus {
  DRAFT = 'DRAFT',
  INDEXED = 'INDEXED',
  PARTIALLY_INDEXED = 'PARTIALLY_INDEXED',
  REJECTED = 'REJECTED',
  OUTDATED = 'OUTDATED',
  ERROR = 'ERROR',
}

export const getStatusForStatusBox = (leasePriceHistory: LeasePriceHistory) => {
  if (leasePriceHistory.status === LeaseAmountUpdateStatus.OUTDATED) return IndexationRowStatus.OUTDATED;
  if (leasePriceHistory.status === LeaseAmountUpdateStatus.ERROR) return IndexationRowStatus.ERROR;
  if (leasePriceHistory.status === LeaseAmountUpdateStatus.DRAFT) return IndexationRowStatus.DRAFT;
  if (DECLINED_PRICE_CHANGE_STATUSES.includes(leasePriceHistory.status)) return IndexationRowStatus.REJECTED;

  const isIndexation = leasePriceHistory.type === LeasePriceHistoryType.INDEXATION;

  const allStatus = (leasePriceHistory.amountDetails ?? []).reduce<LeasePriceHistoryStatus[]>(
    (statuses, currentAmountDetail) => {
      if (isIndexation && currentAmountDetail.rentalStatus !== LeasePriceHistoryStatus.NOT_APPLICABLE) {
        statuses.push(currentAmountDetail.rentalStatus);
      }
      if (currentAmountDetail.monthlyChargesStatus !== LeasePriceHistoryStatus.NOT_APPLICABLE) {
        statuses.push(currentAmountDetail.monthlyChargesStatus);
      }
      return statuses;
    },
    []
  );

  if (allStatus.every((status) => status === LeasePriceHistoryStatus.INDEXED)) return IndexationRowStatus.INDEXED;
  if (allStatus.every((status) => status === LeasePriceHistoryStatus.NON_INDEXED)) return IndexationRowStatus.REJECTED;
  return IndexationRowStatus.PARTIALLY_INDEXED;
};

export const isChargeIndexed = (amountDetail?: LeasePriceHistoryDetail) => {
  const hasNewChargeAmount = (amountDetail?.chargesAmountsDetails ?? []).some(
    (d) => d.newMonthlyAmount !== d.previousMonthlyAmount
  );
  return Boolean(
    amountDetail &&
      hasNewChargeAmount &&
      amountDetail.monthlyChargesStatus &&
      ![LeasePriceHistoryStatus.NON_INDEXED, LeasePriceHistoryStatus.NOT_APPLICABLE].includes(
        amountDetail.monthlyChargesStatus as LeasePriceHistoryStatus
      )
  );
};

/**
 * The amounts a price history applies: the rent or the charges left out of the indexation keep their
 * previous amounts, although the draft still holds the indexed ones.
 */
export const getAppliedAmountDetail = (amountDetail: LeasePriceHistoryDetail): LeasePriceHistoryDetail => {
  const isRentLeftOut = amountDetail.rentalStatus === LeasePriceHistoryStatus.NON_INDEXED;
  const areChargesLeftOut = amountDetail.monthlyChargesStatus === LeasePriceHistoryStatus.NON_INDEXED;
  return {
    ...amountDetail,
    newRentalPrice: isRentLeftOut ? amountDetail.previousRentalPrice : amountDetail.newRentalPrice,
    chargesAmountsDetails: areChargesLeftOut
      ? amountDetail.chargesAmountsDetails?.map((chargeAmountsDetail) => ({
          ...chargeAmountsDetail,
          newMonthlyAmount: chargeAmountsDetail.previousMonthlyAmount,
        }))
      : amountDetail.chargesAmountsDetails,
  };
};

export const isRentIndexed = (amountDetail?: LeasePriceHistoryDetail) => {
  return Boolean(
    amountDetail &&
      amountDetail.newRentalPrice &&
      amountDetail.rentalStatus &&
      ![LeasePriceHistoryStatus.NON_INDEXED, LeasePriceHistoryStatus.NOT_APPLICABLE].includes(
        amountDetail.rentalStatus as LeasePriceHistoryStatus
      )
  );
};

export const getTotalDetailedPriceVariationForLeasePriceHistory = (
  unitLeases: (UnitLease | UnitLeaseDTO)[],
  leasePriceHistory: LeasePriceHistory,
  leaseVariousOperations: (LeaseVariousOperation | LeaseVariousOperationDTO)[] = []
) => {
  const isInitialPrice = leasePriceHistory.type === LeasePriceHistoryType.INITIAL_PRICE;
  const currentPricesAndProposedPricesPerUnit = unitLeases.reduce(
    (acc, currentUnitLease) => {
      const unitId = currentUnitLease.unit!.id;
      const amountDetailForUnit = leasePriceHistory.amountDetails?.find(
        (amountDetail) => amountDetail.unitId === unitId
      );

      const chargesDetails = amountDetailForUnit?.chargesAmountsDetails ?? [];
      const chargeIndexed = isChargeIndexed(amountDetailForUnit) || isInitialPrice;

      const fixedChargeIds = new Set(getFixedChargeLvosForUnit(leaseVariousOperations, unitId).map((lvo) => lvo.id));
      const provisionedChargeIds = new Set(
        getProvisionedChargeLvosForUnit(leaseVariousOperations, unitId).map((lvo) => lvo.id)
      );

      const sumByIds = (ids: Set<string>, field: 'previousMonthlyAmount' | 'newMonthlyAmount') =>
        chargesDetails.reduce(
          (sum, chargesDetail) => (ids.has(chargesDetail.leaseVariousOperationId) ? sum + chargesDetail[field] : sum),
          0
        );

      const previousFixedCharges = sumByIds(fixedChargeIds, 'previousMonthlyAmount');
      const newFixedMonthlyCharges = sumByIds(fixedChargeIds, 'newMonthlyAmount');
      const previousProvisionedCharges = sumByIds(provisionedChargeIds, 'previousMonthlyAmount');
      const newProvisionedMonthlyCharges = sumByIds(provisionedChargeIds, 'newMonthlyAmount');

      const currentRentalPrice = isInitialPrice
        ? currentUnitLease.initialRentalPrice
        : amountDetailForUnit!.previousRentalPrice;
      const newRent =
        isRentIndexed(amountDetailForUnit) || isInitialPrice
          ? amountDetailForUnit!.newRentalPrice!
          : amountDetailForUnit!.previousRentalPrice;

      acc.push({
        id: unitId,
        currentFixedCharges: previousFixedCharges,
        newFixedCharges: chargeIndexed ? newFixedMonthlyCharges : previousFixedCharges,
        currentMonthlyCharges: previousProvisionedCharges,
        newMonthlyCharges: chargeIndexed ? newProvisionedMonthlyCharges : previousProvisionedCharges,
        currentRentalPrice,
        newRentalPrice: newRent,
      });

      return acc;
    },
    [] as {
      id: string;
      currentFixedCharges: number;
      currentMonthlyCharges: number;
      newFixedCharges: number;
      newMonthlyCharges: number;
      currentRentalPrice: number;
      newRentalPrice: number;
    }[]
  );

  const oldTotalRentValue = sumBy(currentPricesAndProposedPricesPerUnit, 'currentRentalPrice');
  const newTotalRentValue = sumBy(currentPricesAndProposedPricesPerUnit, 'newRentalPrice');
  const oldTotalFixedChargesValue = sumBy(currentPricesAndProposedPricesPerUnit, 'currentFixedCharges');
  const newTotalFixedChargesValue = sumBy(currentPricesAndProposedPricesPerUnit, 'newFixedCharges');
  const oldTotalMonthlyChargesValue = sumBy(currentPricesAndProposedPricesPerUnit, 'currentMonthlyCharges');
  const newTotalMonthlyChargesValue = sumBy(currentPricesAndProposedPricesPerUnit, 'newMonthlyCharges');

  return {
    oldTotalRentValue,
    newTotalRentValue,
    oldTotalFixedChargesValue,
    newTotalFixedChargesValue,
    oldTotalMonthlyChargesValue,
    newTotalMonthlyChargesValue,
  };
};

// the string in DB is A++, but special character are not allowed in enum, so we needed this workaround
export const getEpbWithCorrectKeyValue = (energeticPerformance: string) => {
  if (energeticPerformance === EnergeticPerformance.A_PLUS_PLUS) {
    return 'A_PLUS_PLUS';
  }
  if (energeticPerformance === EnergeticPerformance.A_PLUS) {
    return 'A_PLUS';
  }
  return energeticPerformance;
};

export enum IndexationType {
  STANDARD = 'STANDARD',
  EPB_2022 = 'EPB_2022',
  NEW_EPB_WALLONIA_075 = 'NEW_EPB_WALLONIA_075',
  NEW_EPB_WALLONIA_05 = 'NEW_EPB_WALLONIA_05',
  NEW_EPB_WALLONIA_0 = 'NEW_EPB_WALLONIA_0',
  NEW_EPB_BRUSSELS_FIRST_DEGREE = 'NEW_EPB_BRUSSELS_FIRST_DEGREE',
  NEW_EPB_BRUSSELS_SECOND_DEGREE = 'NEW_EPB_BRUSSELS_SECOND_DEGREE',
  NEW_EPB_FLANDERS_FIRST_DEGREE = 'NEW_EPB_FLANDERS_FIRST_DEGREE',
  NEW_EPB_FLANDERS_SECOND_DEGREE = 'NEW_EPB_FLANDERS_SECOND_DEGREE',
}

const goodEpbFlandersAndWallonia = [
  EnergeticPerformance.A_PLUS_PLUS,
  EnergeticPerformance.A_PLUS,
  EnergeticPerformance.A,
  EnergeticPerformance.B,
  EnergeticPerformance.C,
];
const goodEpbBrussels = [
  EnergeticPerformance.A_PLUS_PLUS,
  EnergeticPerformance.A_PLUS,
  EnergeticPerformance.A,
  EnergeticPerformance.B,
  EnergeticPerformance.C,
  EnergeticPerformance.D,
];

export const getIndexationType = (is2022: boolean, epbValue: EnergeticPerformance, region: string) => {
  if (is2022) {
    return IndexationType.EPB_2022;
  }
  if (!isNil(epbValue) && !isNil(region)) {
    return getIndexationTypeBasedOnEpb(epbValue!, region);
  }
  return IndexationType.STANDARD;
};

const getIndexationTypeBasedOnEpb = (epbValue: EnergeticPerformance, region: string) => {
  if (region === EnumMessagesEN.AddressRegion.Brussels) {
    if (goodEpbBrussels.includes(epbValue)) {
      return IndexationType.STANDARD;
    }
    if (epbValue === EnergeticPerformance.E) {
      return IndexationType.NEW_EPB_BRUSSELS_FIRST_DEGREE;
    }
    return IndexationType.NEW_EPB_BRUSSELS_SECOND_DEGREE;
  }
  if (region === EnumMessagesEN.AddressRegion.Flanders) {
    if (goodEpbFlandersAndWallonia.includes(epbValue)) {
      return IndexationType.STANDARD;
    }
    if (epbValue === EnergeticPerformance.D) {
      return IndexationType.NEW_EPB_FLANDERS_FIRST_DEGREE;
    }
    return IndexationType.NEW_EPB_FLANDERS_SECOND_DEGREE;
  }
  if (region === EnumMessagesEN.AddressRegion.Wallonia) {
    if (goodEpbFlandersAndWallonia.includes(epbValue)) {
      return IndexationType.STANDARD;
    }
    if (epbValue === EnergeticPerformance.D) {
      return IndexationType.NEW_EPB_WALLONIA_075;
    }
    if (epbValue === EnergeticPerformance.E) {
      return IndexationType.NEW_EPB_WALLONIA_05;
    }
    return IndexationType.NEW_EPB_WALLONIA_0;
  }
  return IndexationType.STANDARD;
};

export interface IndexationOperationStepsType {
  label: string;
  operationParameters: OperationParameters;
  result: number;
  resType: OperandType;
}

export interface OperationParameters {
  operation: OperationType;
  firstOperand?: number | string;
  firstOperandType?: OperandType;
  secondOperand?: number | string;
  secondOperandType?: OperandType;
  thirdOperand?: number | string;
  thirdOperandType?: OperandType;
  text?: { id: string; epb?: string };
  textFormatted?: string;
}

export enum OperationType {
  SUBTRACTION = 'SUBTRACTION',
  MULTIPLICATION = 'MULTIPLICATION',
  INDEXOPERATION = 'INDEXOPERATION',
  DIVISION = 'DIVISION',
  CORRECTION_FACTOR_FIRST_DEGREE = 'CORRECTION_FACTOR_FIRST_DEGREE',
  CORRECTION_FACTOR_SECOND_DEGREE = 'CORRECTION_FACTOR_SECOND_DEGREE',
  CORRECTION_FACTOR_WALLONIA_0 = 'CORRECTION_FACTOR_WALLONIA_0',
  CORRECTION_FACTOR_WALLONIA_05 = 'CORRECTION_FACTOR_WALLONIA_05',
  CORRECTION_FACTOR_WALLONIA_075 = 'CORRECTION_FACTOR_WALLONIA_075',
  TEXT = 'TEXT',
}

export enum OperandType {
  PERCENT = 'PERCENT',
  CURRENCY = 'CURRENCY',
  CORRECTION_FACTOR = 'CORRECTION_FACTOR',
  NONE = 'NONE',
}

export enum BodyDetailType {
  RENT = 'Rent',
  CHARGES = 'Charges',
}

/**
 * This function will return the steps for the indexation operation.
 * it is used to display the calculation steps in the indexation pdf and the lease price history modal in the frontend.
 * changing one steps here will change it in both places.
 */
export const getIndexationOperationSteps = (
  indexationType: IndexationType,
  isRent: boolean,
  unitLease: UnitLease,
  amountDetail: LeasePriceHistoryDetail,
  chargeIndexationBaseAmounts: Map<string, number>
) => {
  const rentValues = {
    initialPrice: unitLease.initialRentalPrice,
    optimalNewPrice: amountDetail.optimalNewRentalPrice ?? 0,
    reduction: amountDetail.rentReduction ?? 0,
    difference: amountDetail.rentDifference ?? 0,
    indexedAfterReduction: amountDetail.indexedRentAfterReduction ?? 0,
    indexedAfterNewEpbReduction: amountDetail.rentIndexedPriceAfterNewEpbReduction ?? 0,
    previousYearIndexed: amountDetail.previousYearIndexedRent ?? 0,
  };
  const chargesValues = {
    initialPrice: sumChargeIndexationBaseAmountsFromDetail(amountDetail, chargeIndexationBaseAmounts),
    optimalNewPrice: amountDetail.optimalNewMonthlyChargesPrice ?? 0,
    reduction: amountDetail.chargesReduction ?? 0,
    difference: amountDetail.chargesDifference ?? 0,
    indexedAfterReduction: amountDetail.indexedChargesAfterReduction ?? 0,
    indexedAfterNewEpbReduction: getEpbReducedChargesTotalOfDetail(amountDetail) ?? 0,
    previousYearIndexed: amountDetail.previousYearIndexedCharges ?? 0,
  };

  const values = isRent ? rentValues : chargesValues;
  return buildIndexationOperationSteps(indexationType, values, amountDetail);
};

interface IndexationStepValues {
  initialPrice: number;
  optimalNewPrice: number;
  reduction: number;
  difference: number;
  indexedAfterReduction: number;
  indexedAfterNewEpbReduction: number;
  previousYearIndexed: number;
}

const buildIndexationOperationSteps = (
  indexationType: IndexationType,
  values: IndexationStepValues,
  amountDetail: LeasePriceHistoryDetail
): IndexationOperationStepsType[] => {
  const {
    initialPrice,
    optimalNewPrice,
    reduction,
    difference,
    indexedAfterReduction,
    indexedAfterNewEpbReduction,
    previousYearIndexed,
  } = values;

  if (indexationType === IndexationType.NEW_EPB_WALLONIA_0) {
    return [
      {
        label: 'lease.detail.indexationGeneral.correctionFactor',
        operationParameters: {
          operation: OperationType.CORRECTION_FACTOR_WALLONIA_0,
          firstOperand: amountDetail.oldestIndexForCorrectionFactorCalculation ?? 0,
          secondOperand: amountDetail.newestIndexForCorrectionFactorCalculation ?? 1,
        },
        result: amountDetail.correctionFactor ?? 0,
        resType: OperandType.CORRECTION_FACTOR,
      },
      {
        label: 'lease.detail.indexationGeneral.thisYearIndexation',
        operationParameters: {
          operation: OperationType.INDEXOPERATION,
          firstOperand: initialPrice,
          secondOperand: amountDetail.indexationNewIndex ?? 0,
          thirdOperand: amountDetail.indexationBaseIndex ?? 1,
        },
        result: optimalNewPrice,
        resType: OperandType.CURRENCY,
      },
      {
        label: 'lease.detail.indexationGeneral.maximumIndexationAllowedNewEpbRules',
        operationParameters: {
          operation: OperationType.MULTIPLICATION,
          firstOperand: optimalNewPrice,
          secondOperand: amountDetail.correctionFactor ?? 0,
        },
        result: indexedAfterNewEpbReduction,
        resType: OperandType.CURRENCY,
      },
    ];
  }
  if (indexationType === IndexationType.NEW_EPB_WALLONIA_05) {
    return [
      {
        label: 'lease.detail.indexationGeneral.correctionFactor',
        operationParameters: {
          operation: OperationType.CORRECTION_FACTOR_WALLONIA_05,
          firstOperand: amountDetail.oldestIndexForCorrectionFactorCalculation ?? 0,
          secondOperand: amountDetail.newestIndexForCorrectionFactorCalculation ?? 0,
          thirdOperand: amountDetail.newestIndexForCorrectionFactorCalculation ?? 1,
        },
        result: amountDetail.correctionFactor ?? 0,
        resType: OperandType.CORRECTION_FACTOR,
      },
      {
        label: 'lease.detail.indexationGeneral.thisYearIndexation',
        operationParameters: {
          operation: OperationType.INDEXOPERATION,
          firstOperand: initialPrice,
          secondOperand: amountDetail.indexationNewIndex ?? 0,
          thirdOperand: amountDetail.indexationBaseIndex ?? 1,
        },
        result: optimalNewPrice,
        resType: OperandType.CURRENCY,
      },
      {
        label: 'lease.detail.indexationGeneral.maximumIndexationAllowedNewEpbRules',
        operationParameters: {
          operation: OperationType.MULTIPLICATION,
          firstOperand: optimalNewPrice,
          secondOperand: amountDetail.correctionFactor ?? 0,
        },
        result: indexedAfterNewEpbReduction,
        resType: OperandType.CURRENCY,
      },
    ];
  }
  if (indexationType === IndexationType.NEW_EPB_WALLONIA_075) {
    return [
      {
        label: 'lease.detail.indexationGeneral.correctionFactor',
        operationParameters: {
          operation: OperationType.CORRECTION_FACTOR_WALLONIA_075,
          firstOperand: amountDetail.oldestIndexForCorrectionFactorCalculation ?? 0,
          secondOperand: amountDetail.newestIndexForCorrectionFactorCalculation ?? 0,
          thirdOperand: amountDetail.newestIndexForCorrectionFactorCalculation ?? 1,
        },
        result: amountDetail.correctionFactor ?? 0,
        resType: OperandType.CORRECTION_FACTOR,
      },
      {
        label: 'lease.detail.indexationGeneral.thisYearIndexation',
        operationParameters: {
          operation: OperationType.INDEXOPERATION,
          firstOperand: initialPrice,
          secondOperand: amountDetail.indexationNewIndex ?? 0,
          thirdOperand: amountDetail.indexationBaseIndex ?? 1,
        },
        result: optimalNewPrice,
        resType: OperandType.CURRENCY,
      },
      {
        label: 'lease.detail.indexationGeneral.maximumIndexationAllowedNewEpbRules',
        operationParameters: {
          operation: OperationType.MULTIPLICATION,
          firstOperand: optimalNewPrice,
          secondOperand: amountDetail.correctionFactor ?? 0,
        },
        result: indexedAfterNewEpbReduction,
        resType: OperandType.CURRENCY,
      },
    ];
  }
  if (
    indexationType === IndexationType.NEW_EPB_BRUSSELS_FIRST_DEGREE ||
    indexationType === IndexationType.NEW_EPB_FLANDERS_FIRST_DEGREE
  ) {
    return [
      {
        label: 'lease.detail.indexationGeneral.correctionFactor',
        operationParameters: {
          operation: OperationType.CORRECTION_FACTOR_FIRST_DEGREE,
          firstOperand: amountDetail.oldestIndexForCorrectionFactorCalculation ?? 0,
          secondOperand: amountDetail.newestIndexForCorrectionFactorCalculation ?? 0,
          thirdOperand: amountDetail.newestIndexForCorrectionFactorCalculation ?? 1,
        },
        result: amountDetail.correctionFactor ?? 0,
        resType: OperandType.CORRECTION_FACTOR,
      },
      {
        label: 'lease.detail.indexationGeneral.thisYearIndexation',
        operationParameters: {
          operation: OperationType.INDEXOPERATION,
          firstOperand: initialPrice,
          secondOperand: amountDetail.indexationNewIndex ?? 0,
          thirdOperand: amountDetail.indexationBaseIndex ?? 1,
        },
        result: optimalNewPrice,
        resType: OperandType.CURRENCY,
      },
      {
        label: 'lease.detail.indexationGeneral.maximumIndexationAllowedNewEpbRules',
        operationParameters: {
          operation: OperationType.MULTIPLICATION,
          firstOperand: optimalNewPrice,
          secondOperand: amountDetail.correctionFactor ?? 0,
        },
        result: indexedAfterNewEpbReduction,
        resType: OperandType.CURRENCY,
      },
    ];
  }
  if (
    indexationType === IndexationType.NEW_EPB_BRUSSELS_SECOND_DEGREE ||
    indexationType === IndexationType.NEW_EPB_FLANDERS_SECOND_DEGREE
  ) {
    return [
      {
        label: 'lease.detail.indexationGeneral.correctionFactor',
        operationParameters: {
          operation: OperationType.CORRECTION_FACTOR_SECOND_DEGREE,
          firstOperand: amountDetail.oldestIndexForCorrectionFactorCalculation ?? 0,
          secondOperand: amountDetail.newestIndexForCorrectionFactorCalculation ?? 1,
        },
        result: amountDetail.correctionFactor ?? 0,
        resType: OperandType.CORRECTION_FACTOR,
      },
      {
        label: 'lease.detail.indexationGeneral.thisYearIndexation',
        operationParameters: {
          operation: OperationType.INDEXOPERATION,
          firstOperand: initialPrice,
          secondOperand: amountDetail.indexationNewIndex ?? 0,
          thirdOperand: amountDetail.indexationBaseIndex ?? 1,
        },
        result: optimalNewPrice,
        resType: OperandType.CURRENCY,
      },
      {
        label: 'lease.detail.indexationGeneral.maximumIndexationAllowedNewEpbRules',
        operationParameters: {
          operation: OperationType.MULTIPLICATION,
          firstOperand: optimalNewPrice,
          secondOperand: amountDetail.correctionFactor ?? 0,
        },
        result: indexedAfterNewEpbReduction,
        resType: OperandType.CURRENCY,
      },
    ];
  }
  if (indexationType === IndexationType.EPB_2022) {
    return [
      {
        label: 'lease.detail.indexationGeneral.previousYearIndexation',
        operationParameters: {
          operation: OperationType.INDEXOPERATION,
          firstOperand: initialPrice,
          secondOperand: amountDetail.previousYearIndexationNewIndex ?? 0,
          thirdOperand: amountDetail.indexationBaseIndex ?? 1,
        },
        result: previousYearIndexed,
        resType: OperandType.CURRENCY,
      },
      {
        label: 'lease.detail.indexationGeneral.thisYearIndexation',
        operationParameters: {
          operation: OperationType.INDEXOPERATION,
          firstOperand: initialPrice,
          secondOperand: amountDetail.indexationNewIndex ?? 0,
          thirdOperand: amountDetail.indexationBaseIndex ?? 1,
        },
        result: optimalNewPrice,
        resType: OperandType.CURRENCY,
      },
      {
        label: 'lease.detail.indexationGeneral.indexationDifference',
        operationParameters: {
          operation: OperationType.SUBTRACTION,
          firstOperand: optimalNewPrice,
          firstOperandType: OperandType.CURRENCY,
          secondOperand: previousYearIndexed,
          secondOperandType: OperandType.CURRENCY,
        },
        result: difference,
        resType: OperandType.CURRENCY,
      },
      {
        label: 'lease.detail.indexationGeneral.percentIndexationAllowedEpb',
        operationParameters: {
          operation: OperationType.TEXT,
          text: { id: 'lease.detail.indexationGeneral.epbLetter', epb: amountDetail.epb },
        },
        result: amountDetail.reductionPercentage ?? 0,
        resType: OperandType.PERCENT,
      },
      {
        label: 'lease.detail.indexationGeneral.reductionToApply',
        operationParameters: {
          operation: OperationType.MULTIPLICATION,
          firstOperand: difference,
          firstOperandType: OperandType.CURRENCY,
          secondOperand:
            amountDetail.reductionPercentage !== null && amountDetail.reductionPercentage !== undefined
              ? 1 - amountDetail.reductionPercentage
              : 0,
          secondOperandType: OperandType.PERCENT,
        },
        result: reduction,
        resType: OperandType.CURRENCY,
      },
      {
        label: 'lease.detail.indexationGeneral.maximumAllowedIndexation',
        operationParameters: {
          operation: OperationType.SUBTRACTION,
          firstOperand: optimalNewPrice,
          firstOperandType: OperandType.CURRENCY,
          secondOperand: reduction,
          secondOperandType: OperandType.CURRENCY,
        },
        result: indexedAfterReduction,
        resType: OperandType.CURRENCY,
      },
    ] as IndexationOperationStepsType[];
  }
  return [
    {
      label: 'lease.detail.indexationGeneral.thisYearIndexation',
      operationParameters: {
        operation: OperationType.INDEXOPERATION,
        firstOperand: initialPrice,
        secondOperand: amountDetail.indexationNewIndex ?? 0,
        thirdOperand: amountDetail.indexationBaseIndex ?? 1,
      },
      result: optimalNewPrice,
      resType: OperandType.CURRENCY,
    },
  ] as IndexationOperationStepsType[];
};

/** The steps print the detail's base index as the divider: the charge's own base replaces it. */
export const getChargeIndexationOperationSteps = (
  indexationType: IndexationType,
  amountDetail: LeasePriceHistoryDetail,
  chargeBase: ChargeIndexationBase
): IndexationOperationStepsType[] => {
  const baseIndex = chargeBase.baseIndex ?? amountDetail.indexationBaseIndex ?? 1;
  const newIndex = amountDetail.indexationNewIndex ?? 0;
  const correctionFactor = amountDetail.correctionFactor ?? 0;
  const previousYearNewIndex = amountDetail.previousYearIndexationNewIndex ?? 0;
  const reductionPercentage = amountDetail.reductionPercentage ?? 0;

  const initialPrice = chargeBase.baseAmount;
  const optimalNewPrice = getIndexedPrice(baseIndex, newIndex, initialPrice);
  const previousYearIndexed = getIndexedPrice(baseIndex, previousYearNewIndex, initialPrice);
  const difference = roundAtSecondDecimal(optimalNewPrice - previousYearIndexed);
  const reduction = roundAtSecondDecimal(difference * (1 - reductionPercentage));
  const indexedAfterReduction = roundAtSecondDecimal(optimalNewPrice - reduction);
  const indexedAfterNewEpbReduction = roundAtSecondDecimal(optimalNewPrice * correctionFactor);

  return buildIndexationOperationSteps(
    indexationType,
    {
      initialPrice,
      optimalNewPrice,
      reduction,
      difference,
      indexedAfterReduction,
      indexedAfterNewEpbReduction,
      previousYearIndexed,
    },
    { ...amountDetail, indexationBaseIndex: baseIndex }
  );
};

/** Callers pass an indexed `chargeBase`: a not-indexed line has no expected indexed amount. */
export const getEpbReducedChargeAmount = (
  amountDetail: LeasePriceHistoryDetail,
  unitChargeBases: ChargeIndexationBase[],
  chargeBase: ChargeIndexationBase
): number => {
  const newIndex = amountDetail.indexationNewIndex ?? 0;
  const indexCharge = ({ baseIndex, baseAmount }: ChargeIndexationBase) =>
    getIndexedPrice(baseIndex ?? amountDetail.indexationBaseIndex ?? 1, newIndex, baseAmount);
  const totalOptimalFixedCharges = roundAtSecondDecimal(
    sumBy(
      unitChargeBases.filter((base) => base.isIndexed),
      indexCharge
    )
  );
  const epbReducedTotal = amountDetail.chargesIndexedPriceAfterNewEpbReduction;
  const epbReductionFactor =
    !isNil(epbReducedTotal) && totalOptimalFixedCharges > 0 ? epbReducedTotal / totalOptimalFixedCharges : 1;
  return roundAtSecondDecimal(indexCharge(chargeBase) * epbReductionFactor);
};

export const INDEXED_STATUS = [LeasePriceHistoryStatus.INDEXED, LeasePriceHistoryStatus.INDEXED_WITH_CUSTOM_AMOUNT];

export const formatOperand = (opType: OperandType, operand: number | string, language: string) => {
  if (typeof operand === 'string') {
    return operand;
  }
  switch (opType) {
    case OperandType.PERCENT:
      return formatNumberToPercent(operand, language);
    case OperandType.CURRENCY:
      return formatNumberToEuroCurrency(operand, language);
    case OperandType.CORRECTION_FACTOR: {
      const roundedOperand = roundToDecimalPlace(operand, 5);
      return formatNumber(roundedOperand, language);
    }
    default:
      return formatNumber(operand, language);
  }
};

export const getRegionRequirements = (unitLease: UnitLease | UnitLeaseDTO, region?: string | null) => {
  const unitLeaseTypeRequirementsForWalloniaAndBrussels =
    unitLease?.unit?.type === UnitType.RESIDENTIAL || unitLease?.unit?.type === UnitType.STUDENT;
  const unitLeaseTypeRequirementsForFlanders = unitLease?.unit?.type === UnitType.RESIDENTIAL;

  const flandersRequirements = region === EnumMessagesEN.AddressRegion.Flanders && unitLeaseTypeRequirementsForFlanders;
  const walloniaAndBrusselsRequirements =
    region !== EnumMessagesEN.AddressRegion.Flanders && unitLeaseTypeRequirementsForWalloniaAndBrussels;
  return { flandersRequirements, walloniaAndBrusselsRequirements };
};

export interface EpbReductionParams {
  indexPercentage: Map<EnergeticPerformance, number>;
  startDate: CalendarDate;
  endDate: CalendarDate;
}

let epbWalCache: EpbReductionParams | undefined;
export const getEpbWal = (): EpbReductionParams =>
  (epbWalCache ??= {
    indexPercentage: new Map([
      [EnergeticPerformance.A_PLUS_PLUS, 1],
      [EnergeticPerformance.A_PLUS, 1],
      [EnergeticPerformance.A, 1],
      [EnergeticPerformance.B, 1],
      [EnergeticPerformance.C, 1],
      [EnergeticPerformance.D, 0.75],
      [EnergeticPerformance.E, 0.5],
      [EnergeticPerformance.F, 0],
      [EnergeticPerformance.G, 0],
    ]),
    startDate: toCalendarDate('2022-11-01'),
    endDate: toCalendarDate('2023-10-31'),
  });

let epbBxlCache: EpbReductionParams | undefined;
export const getEpbBxl = (): EpbReductionParams =>
  (epbBxlCache ??= {
    indexPercentage: new Map([
      [EnergeticPerformance.A_PLUS_PLUS, 1],
      [EnergeticPerformance.A_PLUS, 1],
      [EnergeticPerformance.A, 1],
      [EnergeticPerformance.B, 1],
      [EnergeticPerformance.C, 1],
      [EnergeticPerformance.D, 1],
      [EnergeticPerformance.E, 0.5],
      [EnergeticPerformance.F, 0],
      [EnergeticPerformance.G, 0],
    ]),
    startDate: toCalendarDate('2022-10-14'),
    endDate: toCalendarDate('2023-10-13'),
  });

let epbFlaCache: EpbReductionParams | undefined;
export const getEpbFla = (): EpbReductionParams =>
  (epbFlaCache ??= {
    indexPercentage: new Map([
      [EnergeticPerformance.A_PLUS_PLUS, 1],
      [EnergeticPerformance.A_PLUS, 1],
      [EnergeticPerformance.A, 1],
      [EnergeticPerformance.B, 1],
      [EnergeticPerformance.C, 1],
      [EnergeticPerformance.D, 0.5],
      [EnergeticPerformance.E, 0],
      [EnergeticPerformance.F, 0],
      [EnergeticPerformance.G, 0],
    ]),
    startDate: toCalendarDate('2022-10-01'),
    endDate: toCalendarDate('2023-09-30'),
  });

export const selectReductionParam = (region: string): EpbReductionParams | undefined => {
  switch (region) {
    case EnumMessagesEN.AddressRegion.Brussels:
      return getEpbBxl();
    case EnumMessagesEN.AddressRegion.Wallonia:
      return getEpbWal();
    case EnumMessagesEN.AddressRegion.Flanders:
      return getEpbFla();
    default:
      return undefined;
  }
};

export const getCurrentAnniversaryDate = (leaseStartDate: CalendarDate): CalendarDate => {
  const currentDate = today();
  const anniversaryDateThisYear = setCalendarDate(leaseStartDate, currentDate.year, 'year');
  const targetYear = isAfter(anniversaryDateThisYear, currentDate) ? currentDate.year - 1 : currentDate.year;
  return setCalendarDate(leaseStartDate, targetYear, 'year');
};

export const getNextAnniversaryDate = (leaseStartDate: CalendarDate): CalendarDate =>
  addCalendarDate(getCurrentAnniversaryDate(leaseStartDate), 1, 'years');

export const nextIndexationMonthDefault = (leaseStartDate: CalendarDate): CalendarDate => {
  const oneYearAfterLeaseStartDate = addCalendarDate(leaseStartDate, 1, 'years');
  const nextAnniversaryDate = getNextAnniversaryDate(leaseStartDate);

  const nextIndexationDate = isAfter(oneYearAfterLeaseStartDate, nextAnniversaryDate)
    ? oneYearAfterLeaseStartDate
    : nextAnniversaryDate;

  return startOfCalendarMonth(nextIndexationDate);
};

export const getEligibleEpbs = (epbs: Technic[], reductionParam: EpbReductionParams, leaseStartDate: CalendarDate) =>
  epbs.filter((epb) => {
    const pebIssueDate = findCustomValue(epb.customValues, TechnicCustomValue.PEB_ISSUE_DATE);
    const rawIssueDate = pebIssueDate ?? epb.createdAt;
    if (isNil(rawIssueDate)) {
      return false;
    }
    const issueDate = pebIssueDate ? toCalendarDate(rawIssueDate) : calendarDateFromTimestamp(rawIssueDate);
    const anniversaryDate = getCurrentAnniversaryDate(leaseStartDate);
    const epbIssuedBeforeReductionStartDate = isAfter(reductionParam.startDate, issueDate);
    const epbIssuedInReductionPeriodBeforeAnniversaryDate =
      isAfter(anniversaryDate, issueDate) && isAfter(issueDate, reductionParam.startDate);
    return epbIssuedBeforeReductionStartDate || epbIssuedInReductionPeriodBeforeAnniversaryDate;
  });

export const INDEX_TYPE_BASE_YEARS = {
  HEALTH: [BaseYear.YEAR_1988, BaseYear.YEAR_1996, BaseYear.YEAR_2004, BaseYear.YEAR_2013, BaseYear.YEAR_2025],
  CONSUMER_PRICE: [
    BaseYear.YEAR_1914,
    BaseYear.YEAR_1936,
    BaseYear.YEAR_1953,
    BaseYear.YEAR_1966,
    BaseYear.YEAR_1971,
    BaseYear.YEAR_1974,
    BaseYear.YEAR_1981,
    BaseYear.YEAR_1988,
    BaseYear.YEAR_1996,
    BaseYear.YEAR_2004,
    BaseYear.YEAR_2013,
    BaseYear.YEAR_2025,
  ],
  CONSUMER_PRICE_NO_ENERGY: [BaseYear.YEAR_2013, BaseYear.YEAR_2025],
};

/** Base year of a lease without advanced indexation: the health base year legally in force at signature. */
export const getIndexationBaseYear = (signatureDate: CalendarDate): BaseYear => {
  if (isBefore(signatureDate, toCalendarDate('1988-01-01'))) {
    return BaseYear.YEAR_1988;
  }
  const baseYearEffectiveFrom = getBaseYearEffectiveFrom();
  const healthBaseYears = INDEX_TYPE_BASE_YEARS.HEALTH;
  const inForceBaseYears = healthBaseYears.filter(
    (baseYear) => !isAfter(baseYearEffectiveFrom[baseYear], signatureDate)
  );
  return last(inForceBaseYears) ?? healthBaseYears[0];
};

export interface IndexationSeries {
  /** Undefined: the legal default, the health index for the new index. */
  indexType?: IndexType;
  baseYear: number;
}

export interface IndexationSeriesInput extends CustomIndexationParams {
  signatureDate: CalendarDate;
}

/**
 * The series and base year a unit is indexed with. A common-law lease is trusted on its type and the unit type is not
 * checked: its custom indexation applies even to a residential unit.
 */
export const resolveIndexationSeries = ({
  signatureDate,
  ...customIndexation
}: IndexationSeriesInput): IndexationSeries =>
  isViableCustomIndexation(customIndexation)
    ? { indexType: customIndexation.indexType, baseYear: customIndexation.baseYear }
    : { baseYear: getIndexationBaseYear(signatureDate) };

export const shouldShowBaseIndexDateWarning = (
  baseIndexType: BaseIndexType,
  leaseStartDate: CalendarDate,
  baseIndexDate?: CalendarDate | null
): boolean => {
  if (baseIndexType !== BaseIndexType.CUSTOM_DATE || !baseIndexDate) {
    return false;
  }

  const normalizedBaseDate = startOfCalendarMonth(baseIndexDate);
  const currentMonth = startOfCalendarMonth(today());
  const normalizedStartDate = startOfCalendarMonth(leaseStartDate);

  const firstAnniversary = addCalendarDate(normalizedStartDate, 1, 'years');

  const shouldCompareWithFirstAnniversary =
    isAfter(normalizedStartDate, currentMonth) || isAfter(firstAnniversary, currentMonth);

  const maxAllowedBaseDate = shouldCompareWithFirstAnniversary ? firstAnniversary : currentMonth;

  return isAfterOrEqual(normalizedBaseDate, maxAllowedBaseDate);
};

export const isLeaseStartDateBeforeReductionPeriod = (leaseStartDate: CalendarDate, region: string): boolean => {
  const reductionParam = selectReductionParam(region);
  if (!reductionParam) {
    return false;
  }
  return isBefore(leaseStartDate, reductionParam.startDate);
};

export const getRegion = (lease: LeaseExtended, buildings: Building[]): string => {
  const buildingId = lease.units![0].unit!.buildingId;
  const building = buildings.find((leaseBuilding) => leaseBuilding.id === buildingId);
  const value = building?.address?.region;

  if (!value) {
    return '';
  }
  return value;
};

export const getFirstDayOfLastMonth = (date: CalendarDate): CalendarDate => {
  const lastMonth = subtractCalendarDate(date, 1, 'months');
  return startOfCalendarMonth(lastMonth);
};

export interface NextLeaseIndexation {
  indexationDate: CalendarDate;
  newIndexMonth: CalendarDate;
}

/**
 * The first lease anniversary in a month after `from`'s month:
 * a line starting in the anniversary month has no elapsed month to index.
 */
export const getNextLeaseIndexation = (leaseStartDate: CalendarDate, from: CalendarDate): NextLeaseIndexation => {
  const thisYear = setCalendarDate(leaseStartDate, from.year, 'year');
  const indexationDate =
    thisYear.month > from.month ? thisYear : setCalendarDate(leaseStartDate, from.year + 1, 'year');
  return { indexationDate, newIndexMonth: getNewIndexMonth(leaseStartDate, indexationDate) };
};
